import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSwrTick } from '@/lib/swrRefresh';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, PieChart, ChevronUp, ChevronDown, Scale, Maximize2, Minimize2, X } from 'lucide-react';
import PortfolioActionLink from '@/components/PortfolioActionLink';
import ModalCloseButton from '@/components/ModalCloseButton';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';
import { advancePortfolioLoadStage, usePortfolioLoadStage } from '@/lib/portfolioLoadOrder';  // eslint-disable-line no-unused-vars

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
  { key: '6m', label: '6M' },
  { key: '1y', label: '1Y' },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Round 95bw v2 (Jun 2026) — Per-month color palette for the 1M view's
// date labels and the color-key strip underneath. Each entry is
// [lightThemeHex, darkThemeHex] tuned so the same month reads cleanly
// on both backgrounds. The user picked May=pink + Jun=orange as the
// anchors; the rest follow a 12-step hue rotation that keeps adjacent
// months distinct from each other.
const MONTH_COLORS = [
  ['#1d4ed8', '#60a5fa'], // Jan — blue
  ['#0891b2', '#22d3ee'], // Feb — cyan
  ['#15803d', '#4ade80'], // Mar — green
  ['#65a30d', '#a3e635'], // Apr — lime
  ['#be185d', '#f472b6'], // May — pink  (user-anchored)
  ['#c2410c', '#fb923c'], // Jun — orange (user-anchored)
  ['#dc2626', '#f87171'], // Jul — red
  ['#d97706', '#fbbf24'], // Aug — amber
  ['#7c3aed', '#a78bfa'], // Sep — violet
  ['#9333ea', '#c084fc'], // Oct — purple
  ['#4f46e5', '#818cf8'], // Nov — indigo
  ['#0e7490', '#06b6d4'], // Dec — teal
];

// Theme-aware sector colors: [light, dark]
const SECTOR_COLORS = [
  ['#0d9488', '#2dd4bf'], // teal
  ['#7c3aed', '#a78bfa'], // violet
  ['#2563eb', '#60a5fa'], // blue
  ['#c2410c', '#fb923c'], // orange
  ['#be185d', '#f472b6'], // pink
  ['#15803d', '#4ade80'], // green
  ['#b45309', '#fbbf24'], // amber
  ['#4338ca', '#818cf8'], // indigo
  ['#0e7490', '#22d3ee'], // cyan
  ['#9f1239', '#fb7185'], // rose
];

function formatValue(val) {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
  return `$${val.toFixed(0)}`;
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// "2026-08" → "Aug 2026" for the daily-payer rollup ▲ modal header.
function formatMonthLabel(ym) {
  if (!ym || ym.length < 7) return ym || '';
  const [y, m] = ym.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

// ─── Portfolio Value Chart ───
function ValueChart({ isDark, refreshKey }) {
  const [period, setPeriod] = useState('3m');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [markerTooltip, setMarkerTooltip] = useState(null);
  // Round P146·5 — persistent dividend-payouts modal (daily-payer
  // monthly ▲ rollups + tap-to-open on regular ● dots for phones).
  // Only closes via the X button, backdrop click, or Escape.
  const [divModal, setDivModal] = useState(null);
  useEffect(() => {
    if (!divModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setDivModal(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [divModal]);
  // Round 95bv (Jun 2026) — Desktop "expand" toggle. When true, the
  // outer card is repositioned to `fixed inset-x-4 inset-y-4 z-50`
  // so it fills the viewport — the SVG inside is viewBox-based with
  // `w-full h-auto`, so the chart line, markers, and hover hit-targets
  // all scale proportionally larger, making overlapping ▲/▼/● easier
  // to hover individually. Hidden on phone view via `hidden md:flex`
  // on the toggle button so mobile users never see (or need) this.
  const [isExpanded, setIsExpanded] = useState(false);
  // Round 95bw v2 (Jun 2026) — Smart period clamp. The backend tells
  // us the EFFECTIVE period it actually rendered (clamped to the
  // portfolio's age) via `data.effective_period`. We highlight that
  // button rather than the one the user clicked, so the UI stays
  // honest with the chart contents.
  // Escape key closes the expanded view — standard modal affordance
  // so users don't have to hunt for the X button or click backdrop.
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded]);
  // Toggleable layers — default: value + cost basis on, benchmark off,
  // purchase ▲ + sell ▼ + dividend dots all on. Clicking the colored
  // dot in the legend toggles each layer independently.
  const [layers, setLayers] = useState({
    value: true,
    cost: true,
    benchmark: false,
    purchases: true,
    sells: true,
    dividends: true,
  });
  const toggleLayer = (key) => setLayers(L => ({ ...L, [key]: !L[key] }));

  // ─── Date-pair comparison form ──────────────────────────────────────
  // Lets the user pick two dates inside the visible chart period and
  // see exactly the $ delta + % gain between them. Each date is split
  // into a Month dropdown + Day number input with up/down arrows.
  // The day field auto-clamps to the days-in-month for the selected
  // month (e.g., switching to April after picking 31 snaps to 30).
  // Out-of-range picks raise a toast pointing to the valid window.
  const [d1, setD1] = useState({ year: '', month: '', day: '' });
  const [d2, setD2] = useState({ year: '', month: '', day: '' });
  const scopeParams = usePortfolioScopeParams();
  const scopeKey = scopeParams.portfolio_id || 'all';

  // Feb 2026 (Round P111f) — Benchmark Ticker Choice. Compare against
  // SPY / VOO / QQQ (broad-market) or SCHD / VYM / DGRO (dividend-
  // focused). Choice is passed to the backend on every request AND
  // persisted server-side so it survives sign-out / device swap.
  const BENCHMARK_OPTIONS = ['SPY', 'VOO', 'QQQ', 'SCHD', 'VYM', 'DGRO'];
  const [benchmarkTicker, setBenchmarkTicker] = useState(() => {
    try { return localStorage.getItem('div_benchmark_ticker') || 'SPY'; }
    catch { return 'SPY'; }
  });
  useEffect(() => {
    // Prime from server on mount so a signed-in user sees their
    // saved choice even on a new device / after a cache wipe.
    axios.get(`${API}/portfolio/benchmark-preference`, { withCredentials: true })
      .then(res => {
        const val = res?.data?.benchmark_ticker;
        if (val && BENCHMARK_OPTIONS.includes(val)) {
          setBenchmarkTicker(val);
          try { localStorage.setItem('div_benchmark_ticker', val); } catch { /* ignore */ }
        }
      }).catch(() => { /* silent — fall back to local default */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const changeBenchmark = (val) => {
    if (!BENCHMARK_OPTIONS.includes(val)) return;
    setBenchmarkTicker(val);
    try { localStorage.setItem('div_benchmark_ticker', val); } catch { /* ignore */ }
    axios.post(`${API}/portfolio/benchmark-preference`,
      { benchmark_ticker: val }, { withCredentials: true }
    ).catch(() => { /* ignore — local state still updated */ });
  };

  // Round 87 (Feb 2026) — Silent background refresh on period change
  // and live-price ticks. Only show the spinner on the *initial* load
  // (when we have no data). Subsequent refetches render the new data
  // in-place without unmounting the chart, preventing the card from
  // blinking and shifting layout on mobile.
  // Round P124 (Feb 2026) — Gate the Portfolio Value chart on the
  // shared load-order counter. This is stage 5 (last) because
  // `/portfolio/value-history` + benchmark are the slowest calls
  // on the page (~1.5 s each). Delaying them until every other
  // card has data prevents them from starving the fast fetches
  // via the browser's 6-concurrent HTTP cap.
  const portfolioLoadStage = usePortfolioLoadStage();
  const reqSeq = useRef(0);

  // Portfolio switch: drop the previous portfolio's data immediately so
  // nothing from another portfolio can linger while the new fetch runs.
  const prevScopeKey = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKey.current === scopeKey) return;
    prevScopeKey.current = scopeKey;
    setData(null);
  }, [scopeKey]);

  const swrTick = useSwrTick('/portfolio/value-history');
  useEffect(() => {
    if (portfolioLoadStage < 5) return undefined;
    const seq = ++reqSeq.current;
    const isInitial = data === null;
    if (isInitial) setLoading(true);
    axios.get(`${API}/portfolio/value-history`, { params: { period, benchmark: benchmarkTicker, ...scopeParams }, withCredentials: true })
      .then(res => { if (seq === reqSeq.current) setData(res.data); })
      .catch(() => { if (seq === reqSeq.current && isInitial) setData(null); })
      .finally(() => { if (seq === reqSeq.current && isInitial) setLoading(false); });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, refreshKey, scopeKey, benchmarkTicker, portfolioLoadStage, swrTick]);

  // Round 95bw v3 (Jun 2026) — One-shot "your view got clamped" toast.
  // Brand-new users who pick 1Y on a 3-week-old portfolio shouldn't be
  // left wondering why nothing changed. We fire ONE friendly toast per
  // browser session the first time the backend reports an
  // effective_period that differs from the requested one. After that
  // the silent button-highlight + hint copy carry the message so we
  // never nag.
  useEffect(() => {
    if (!data?.effective_period || !data?.requested_period) return;
    if (data.effective_period === data.requested_period) return;
    try {
      if (sessionStorage.getItem('dividrip:value-clamp-toast') === '1') return;
      sessionStorage.setItem('dividrip:value-clamp-toast', '1');
    } catch { /* sessionStorage blocked → still show, just won't suppress next time */ }
    const reqLabel = PERIODS.find(p => p.key === data.requested_period)?.label || (data.requested_period || '').toUpperCase();
    const effLabel = PERIODS.find(p => p.key === data.effective_period)?.label || (data.effective_period || '').toUpperCase();
    toast.info(`Showing the last ${effLabel} instead of ${reqLabel}`, {
      description: `Your portfolio isn't ${reqLabel} old yet — the chart automatically snaps to the widest range it has data for so the line never starts at $0 just because the window is bigger than your history.`,
      duration: 6500,
    });
  }, [data?.effective_period, data?.requested_period]);

  const chart = useMemo(() => {
    if (!data?.points || data.points.length < 2) return null;
    const pts = data.points;
    const cost = data.cost_basis || [];
    const bench = data.benchmark || [];

    // Y-axis scale considers every visible series so all three lines
    // fit on the same chart without clipping. Series that are toggled
    // OFF don't influence the scale (so toggling SPY off auto-zooms
    // back into the value range).
    const valuesForScale = [];
    if (layers.value) valuesForScale.push(...pts.map(p => p.value));
    if (layers.cost && cost.length) valuesForScale.push(...cost.map(p => p.value));
    if (layers.benchmark && bench.length) valuesForScale.push(...bench.map(p => p.value));
    if (valuesForScale.length === 0) valuesForScale.push(...pts.map(p => p.value));
    const minV = Math.min(...valuesForScale) * 0.95;
    const maxV = Math.max(...valuesForScale) * 1.05;
    const range = maxV - minV || 1;

    // Round 95bw v2 (Jun 2026) — Bumped right padding from 10 → 36 so
    // the rightmost x-axis label ("Jun 16", "Jun 17") no longer gets
    // clipped by the SVG viewBox. Applies to both the compact card
    // and the expanded modal — the SVG is viewBox-based so layout
    // scales proportionally either way.
    const w = 520, h = 200, pl = 55, pr = 36, pt = 10, pb = 30;
    const cw = w - pl - pr, ch = h - pt - pb;

    const projectY = (v) => pt + ch - ((v - minV) / range) * ch;

    const points = pts.map((p, i) => ({
      x: pl + (i / (pts.length - 1)) * cw,
      y: projectY(p.value),
      ...p,
    }));
    const costPts = cost.map((p, i) => ({
      x: pl + (i / Math.max(1, cost.length - 1)) * cw,
      y: projectY(p.value),
      ...p,
    }));
    const benchPts = bench.map((p, i) => ({
      x: pl + (i / Math.max(1, bench.length - 1)) * cw,
      y: projectY(p.value),
      ...p,
    }));

    const lineD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const areaD = lineD + ` L ${points[points.length-1].x} ${pt + ch} L ${points[0].x} ${pt + ch} Z`;
    const costD = costPts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const benchD = benchPts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    const yTicks = [];
    for (let i = 0; i <= 4; i++) {
      const val = minV + (range * i) / 4;
      const y = pt + ch - (i / 4) * ch;
      yTicks.push({ val: formatValue(val), y });
    }

    // Round 95bw v2 (Jun 2026) — X-axis labels are now PERIOD-aware:
    //   • 1Y  → one tick per month transition, label is the month
    //           abbreviation only ("May", "Jun", ...).
    //   • 1M  → every ~3-4 days, each label tinted by the month color
    //           palette above so a multi-month window reads visually.
    //   • 3M/6M → existing every-N-points behavior with full "Mon Day"
    //           labels (unchanged at the user's request).
    // Every tick carries a `month` (0-11) and a `colored` flag so the
    // renderer can decide whether to color the text.
    const xTicks = [];
    const effPeriod = (data && data.effective_period) || period;
    const periodKey = (effPeriod || '').toLowerCase();
    const buildTick = (i, label, opts = {}) => {
      const d = new Date(pts[i].date + 'T00:00:00');
      return {
        label,
        x: points[i].x,
        month: d.getMonth(),
        colored: !!opts.colored,
      };
    };
    if (periodKey === '1y') {
      // One tick at every first-occurrence of a (year, month) pair so
      // labels line up with the start of each month in the data. Label
      // is just the month abbreviation — no day number, per user spec.
      // Downsample to ~8-9 ticks if the window spans a full year+ so
      // adjacent labels never visually collide.
      const all = [];
      const seen = new Set();
      for (let i = 0; i < pts.length; i++) {
        const d = new Date(pts[i].date + 'T00:00:00');
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (!seen.has(key)) {
          seen.add(key);
          all.push(buildTick(i, MONTHS[d.getMonth()], { colored: false }));
        }
      }
      const downStep = all.length > 9 ? Math.ceil(all.length / 8) : 1;
      for (let i = 0; i < all.length; i += downStep) xTicks.push(all[i]);
      // Always include the latest month so the right edge has a label.
      if (all.length > 0 && xTicks[xTicks.length - 1] !== all[all.length - 1]) {
        xTicks.push(all[all.length - 1]);
      }
    } else if (periodKey === '1m') {
      // Round 95bw v3 (Jun 2026) — Anchor 4 dates per calendar month
      // at days [1, 9, 16, 24] (spacing ≈ 8 / 7 / 8 / 7 across the
      // month). Only anchors that fall inside the visible window are
      // rendered, so a 31-day window straddling two months naturally
      // shows 1-3 ticks from one and 3-4 from the other — never the
      // right-edge "JunJ17 18" pile-up we had with step-based ticks.
      // If an anchor day is a weekend/holiday (no data point), snap
      // to the next available trading day so the tick still appears.
      // Round 95bw v3 (Jun 2026) — 4 anchors per month at days
      // [1, 9, 16, 24] (≈ 8 / 7 / 8 / 7 spacing). Tried 5-per-month
      // briefly in v4 but the 4-per-month density read cleaner.
      const anchorDays = [1, 9, 16, 24];
      const startIso = pts[0].date;
      const endIso = pts[pts.length - 1].date;
      const monthKeys = new Set();
      for (const p of pts) {
        const d = new Date(p.date + 'T00:00:00');
        monthKeys.add(`${d.getFullYear()}-${d.getMonth()}`);
      }
      const usedIdx = new Set();
      const candidates = [];
      for (const key of monthKeys) {
        const [yStr, mStr] = key.split('-');
        const y = parseInt(yStr, 10);
        const m = parseInt(mStr, 10);
        for (const day of anchorDays) {
          const target = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          if (target >= startIso && target <= endIso) candidates.push(target);
        }
      }
      candidates.sort();
      for (const target of candidates) {
        const idx = pts.findIndex(p => p.date >= target);
        if (idx >= 0 && !usedIdx.has(idx)) {
          usedIdx.add(idx);
          xTicks.push(buildTick(idx, formatDateLabel(pts[idx].date), { colored: true }));
        }
      }
    } else {
      // 3M / 6M — unchanged behavior, full "Mon Day" labels.
      const step = Math.max(1, Math.floor(pts.length / 6));
      for (let i = 0; i < pts.length; i += step) {
        xTicks.push(buildTick(i, formatDateLabel(pts[i].date), { colored: false }));
      }
    }

    // Round 95bw v2 (Jun 2026) — Month "color key" strip rendered just
    // below the x-axis on the 1M view. One pill per distinct month
    // present in the data, anchored to the midpoint of that month's
    // data points so it sits visually under the dates it represents.
    let monthLegend = [];
    if (periodKey === '1m') {
      const groups = new Map();
      for (let i = 0; i < pts.length; i++) {
        const d = new Date(pts[i].date + 'T00:00:00');
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (!groups.has(key)) groups.set(key, { month: d.getMonth(), startX: points[i].x, endX: points[i].x });
        else groups.get(key).endX = points[i].x;
      }
      monthLegend = Array.from(groups.values()).map(g => ({
        month: g.month,
        midX: (g.startX + g.endX) / 2,
        label: MONTHS[g.month],
      }));
    }

    // Map each event onto the matching SVG x-coord. Group by date so
    // multi-ticker DCA days don't render overlapping shapes.
    const dateToPoint = new Map(points.map(p => [p.date, p]));

    const groupBy = (events) => {
      const groups = new Map();
      for (const ev of (events || [])) {
        const anchor = dateToPoint.get(ev.date);
        if (!anchor) continue;
        if (!groups.has(ev.date)) groups.set(ev.date, { x: anchor.x, y: anchor.y, items: [] });
        groups.get(ev.date).items.push(ev);
      }
      return Array.from(groups.values());
    };
    const purchaseMarkers = groupBy(data.purchases);
    const sellMarkers = groupBy(data.sells);
    // Round P146·5 (Jun 2026) — Daily-payer rollup. A daily payer like
    // SATA lands ~21 payout events per month; rendering a dot per
    // payout buries the chart. Any ticker with ≥6 payouts inside one
    // calendar month (weeklies max out at 5) gets ONE blue ▲ per
    // month instead, anchored at that month's latest visible payout
    // (so it advances daily as new payouts arrive). Clicking the ▲
    // opens a persistent, scrollable modal listing every payout.
    const byTickerMonth = new Map();
    for (const ev of (data.dividends || [])) {
      const key = `${ev.ticker}|${(ev.date || '').slice(0, 7)}`;
      if (!byTickerMonth.has(key)) byTickerMonth.set(key, []);
      byTickerMonth.get(key).push(ev);
    }
    const regularDivEvents = [];
    const dividendRollups = [];
    for (const [key, evs] of byTickerMonth) {
      if (evs.length >= 6) {
        const sorted = evs.slice().sort((a, b) => (a.date < b.date ? -1 : 1));
        let anchor = null;
        for (let i = sorted.length - 1; i >= 0; i--) {
          const p = dateToPoint.get(sorted[i].date);
          if (p) { anchor = p; break; }
        }
        if (!anchor) continue;
        const [tk, ym] = key.split('|');
        dividendRollups.push({
          x: anchor.x, y: anchor.y,
          ticker: tk, month: ym,
          items: sorted,
          total: sorted.reduce((s, e) => s + (Number(e.amount) || 0), 0),
        });
      } else {
        regularDivEvents.push(...evs);
      }
    }
    const dividendDots = groupBy(regularDivEvents);

    const isPositive = data.change >= 0;
    return {
      points, costPts, benchPts,
      lineD, areaD, costD, benchD,
      yTicks, xTicks, monthLegend, w, h, pl, pt, cw, ch, isPositive,
      purchaseMarkers, sellMarkers, dividendDots, dividendRollups,
    };
  }, [data, layers, period]);

  const handleMouseMove = useCallback((e) => {
    if (!chart || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * chart.w;
    let closest = null, closestIdx = -1, dist = Infinity;
    for (let i = 0; i < chart.points.length; i++) {
      const p = chart.points[i];
      const d = Math.abs(p.x - svgX);
      if (d < dist) { dist = d; closest = p; closestIdx = i; }
    }
    if (closest && dist < 25) {
      const cb = chart.costPts[closestIdx]?.value ?? null;
      const bp = chart.benchPts[closestIdx]?.value ?? null;
      const gain = cb !== null ? closest.value - cb : null;
      const gainPct = (cb && cb > 0) ? (gain / cb * 100) : null;
      // shares-held + per-ticker breakdown is not in the response per
      // point (would bloat the payload); the tooltip just summarises
      // value, cost-basis, gain $ + %, and benchmark.
      setTooltip({
        date: formatDateLabel(closest.date),
        value: closest.value,
        costBasis: cb,
        benchmark: bp,
        gain,
        gainPct,
        svgX: closest.x, svgY: closest.y,
        screenX: (closest.x / chart.w) * rect.width,
        screenY: (closest.y / chart.h) * rect.height,
      });
    } else setTooltip(null);
  }, [chart]);

  const lineColor = chart?.isPositive ? '#22c55e' : '#ef4444';
  const areaColor = chart?.isPositive ? (isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)') : (isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.08)');
  const costColor = isDark ? '#94a3b8' : '#64748b';   // slate
  const benchColor = isDark ? '#a78bfa' : '#7c3aed';  // violet
  const purchaseColor = '#f59e0b';                     // amber
  const sellColor = '#ef4444';                         // red
  const dividendColor = '#0ea5e9';                     // sky

  // Legend dots — clicking toggles a layer on/off. Each dot reflects
  // its layer's color, opacity dimmed when off, ring highlight on hover.
  const LegendDot = ({ keyName, color, label, count }) => {
    const on = layers[keyName];
    return (
      <button
        type="button"
        onClick={() => toggleLayer(keyName)}
        className={`group inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-all ${on ? '' : 'opacity-40'} hover:bg-muted/50`}
        title={on ? `Hide ${label}` : `Show ${label}`}
        data-testid={`chart-legend-${keyName}`}
      >
        <span
          className="w-2.5 h-2.5 rounded-full ring-1 ring-offset-1 ring-offset-card transition-shadow"
          style={{ backgroundColor: color, boxShadow: on ? `0 0 0 1.5px ${color}55` : 'none' }}
        />
        <span className="text-foreground">{label}</span>
        {typeof count === 'number' && (
          <span className="text-muted-foreground/70 font-mono">({count})</span>
        )}
      </button>
    );
  };

  // ─── Compare-form helpers ─────────────────────────────────────────
  // The valid window: from the first non-zero point (= start of
  // contributions) through the last available trading day. Anything
  // outside that pops a friendly toast with the boundaries.
  const compareRange = useMemo(() => {
    if (!data?.points || data.points.length === 0) return null;
    const firstNonZero = data.points.find(p => p.value > 0) || data.points[0];
    return {
      earliest: firstNonZero.date,
      latest: data.points[data.points.length - 1].date,
      points: data.points,
    };
  }, [data]);

  // Year list — current year back to 1950, descending so the most
  // recent year sits at the top of the dropdown. Memoised so the
  // ~76-entry array doesn't get rebuilt every render.
  const yearList = useMemo(() => {
    const cur = new Date().getFullYear();
    const out = [];
    for (let y = cur; y >= 1950; y--) out.push(y);
    return out;
  }, []);

  const daysInMonth = useCallback((year, month) => {
    if (!year || !month) return 31;
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    if (!y || !m) return 31;
    // 0-th day of next month = last day of current month — a clean
    // browser-native way to get days-in-month with leap-year handling.
    return new Date(y, m, 0).getDate();
  }, []);

  // Resolve a (year, month, day) pick to an actual date inside the
  // chart. Snaps to the nearest trading day on or after the picked
  // date so weekend/holiday picks always land on a real data point.
  // Returns {iso, point, snapped} or {outOfRange, iso} when invalid.
  const resolvePick = useCallback((pick) => {
    if (!compareRange || !pick.year || !pick.month || !pick.day) return null;
    const y = parseInt(pick.year, 10);
    const m = parseInt(pick.month, 10);
    const day = Math.max(1, Math.min(parseInt(pick.day, 10) || 0, daysInMonth(pick.year, pick.month)));
    const iso = `${y}-${String(m).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    if (iso < compareRange.earliest || iso > compareRange.latest) return { outOfRange: true, iso };
    const point = compareRange.points.find(p => p.date >= iso);
    if (!point) return { outOfRange: true, iso };
    return { iso, point, snapped: point.date !== iso };
  }, [compareRange, daysInMonth]);

  const fmtIso = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // Generic field updater that auto-clamps the day to the month's
  // length whenever the year or month changes (so picking Feb after
  // selecting day 31 snaps day to 28/29, and picking April after 31
  // snaps day to 30).
  const updatePick = (which, patch) => {
    const setter = which === 1 ? setD1 : setD2;
    const cur = which === 1 ? d1 : d2;
    const merged = { ...cur, ...patch };
    if ('year' in patch || 'month' in patch) {
      const max = daysInMonth(merged.year, merged.month);
      if (merged.day && Number(merged.day) > max) merged.day = String(max);
    }
    setter(merged);
  };

  const handleDayChange = (which, raw) => {
    const cur = which === 1 ? d1 : d2;
    if (raw === '') return updatePick(which, { day: '' });
    const max = daysInMonth(cur.year, cur.month);
    const n = Math.max(1, Math.min(parseInt(raw, 10) || 1, max));
    updatePick(which, { day: String(n) });
  };

  const bumpDay = (which, delta) => {
    const cur = which === 1 ? d1 : d2;
    const max = daysInMonth(cur.year, cur.month);
    const cur_day = parseInt(cur.day, 10) || 0;
    const next = Math.max(1, Math.min(cur_day + delta, max));
    updatePick(which, { day: String(next) });
  };

  // Toast on out-of-range whenever all three inputs are filled.
  useEffect(() => {
    if (!compareRange) return;
    [{ which: 1, pick: d1 }, { which: 2, pick: d2 }].forEach(({ which, pick }) => {
      if (!pick.year || !pick.month || !pick.day) return;
      const r = resolvePick(pick);
      if (r?.outOfRange) {
        toast.error(`Date ${which} is outside this view`, {
          description: `Pick something between ${fmtIso(compareRange.earliest)} and ${fmtIso(compareRange.latest)}.`,
          duration: 4000,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d1.year, d1.month, d1.day, d2.year, d2.month, d2.day, compareRange]);

  const compareResult = useMemo(() => {
    const r1 = resolvePick(d1);
    const r2 = resolvePick(d2);
    if (!r1?.point || !r2?.point) return null;
    const v1 = r1.point.value;
    const v2 = r2.point.value;
    const delta = v2 - v1;
    const pct = v1 > 0 ? (delta / v1) * 100 : null;
    return { r1, r2, v1, v2, delta, pct };
  }, [d1, d2, resolvePick]);

  return (
    <>
      {/* Round 95bv (Jun 2026) — Translucent backdrop when expanded.
          Clicking it closes the expanded view (same as the X button). */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm hidden md:block"
          onClick={() => setIsExpanded(false)}
          data-testid="value-chart-expand-backdrop"
          aria-hidden="true"
        />
      )}
      <div
        className={isExpanded
          ? "fixed left-[2.5vw] right-[2.5vw] top-[3vh] bottom-[3vh] z-50 rounded-lg border bg-card p-6 overflow-auto shadow-2xl"
          : "rounded-lg border bg-card p-4 flex-1 min-w-[300px]"
        }
        data-testid="portfolio-value-chart"
      >
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-1.5">
            <Coins className="w-4 h-4 text-emerald-500 dark:text-emerald-400 shrink-0" />
            <span>Portfolio Value</span>
          </h3>
          {data && (
            <>
              <p
                className="text-2xl font-bold font-mono mt-1"
                title="Total portfolio value (stocks + cash) right now."
                data-testid="portfolio-value-headline"
              >
                {formatValue(data.current_total_value ?? data.current_value)}
              </p>
              <p
                className={`text-xs font-mono font-medium cursor-help ${data.change >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                title={`Lifetime net gain — matches the Portfolio Summary card.\n\nNet Gain $ = Total Portfolio Value − Net Contributions\nTotal Portfolio Value: $${(data.current_total_value ?? 0).toFixed(2)}\nNet Contributions: $${(data.net_contributions ?? 0).toFixed(2)}\n\nNet Contributions is everything you've put in (outside deposits + outside-funded buys) minus everything you've withdrawn back to your bank. DRIP buys and recycled sale proceeds are excluded so reinvested dividends count as portfolio gain, not as new contributions.`}
              >
                {data.change >= 0 ? '+' : '−'}{formatValue(Math.abs(data.change))} ({data.change_pct >= 0 ? '+' : ''}{data.change_pct}% all-time)
              </p>
            </>
          )}
        </div>
        {/* Round 95bv (Jun 2026) — Period buttons + Expand toggle.
            The toggle sits to the RIGHT of the 1Y button with a small
            gap so it reads as a separate affordance, not part of the
            timeline. Hidden on mobile (`hidden md:inline-flex`) since
            the user explicitly excluded phone view from this change. */}
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5" data-testid="value-period-buttons">
            {PERIODS.map(p => {
              // Round 95bw v2 — highlight whatever the BACKEND actually
              // rendered (effective_period). When the user clicks 1Y on
              // a 2-month-old portfolio, the backend clamps to "1m" and
              // we highlight 1M here so the button state never lies
              // about what's on screen.
              const active = (data?.effective_period || period) === p.key;
              const clamped = data?.effective_period && data.effective_period !== period && data.effective_period === p.key;
              return (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  data-testid={`value-period-${p.key}`}
                  title={clamped
                    ? `Your portfolio isn't old enough for ${p.label === '1M' ? '1 month' : p.label === '3M' ? '3 months' : p.label === '6M' ? '6 months' : '1 year'} yet, so the chart is showing the widest range it has data for (${p.label}).`
                    : `Zoom the chart line + markers to the last ${p.label}. The headline value and net gain above are always lifetime — the period selector only controls the visualization.`}
                  className={`px-2.5 py-1 text-[10px] font-bold rounded-md transition-colors cursor-help ${
                    active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >{p.label}</button>
              );
            })}
          </div>
          {/* Feb 2026 (Round P111f) — Benchmark ticker dropdown.
              Compares your Value line against a chosen ETF. Persists
              server-side via /portfolio/benchmark-preference and
              refetches automatically when changed. */}
          <select
            value={benchmarkTicker}
            onChange={(e) => changeBenchmark(e.target.value)}
            data-testid="benchmark-ticker-select"
            title="Choose which ETF the dashed comparison line represents. SPY / VOO / QQQ track the broad market; SCHD / VYM / DGRO track dividend-focused baskets."
            className="text-[10px] font-bold rounded-md border border-border bg-card text-foreground hover:bg-muted px-1.5 py-1 cursor-pointer transition-colors"
          >
            {BENCHMARK_OPTIONS.map(t => (
              <option key={t} value={t} className="bg-background text-foreground">{t}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="hidden md:inline-flex items-center justify-center p-1 rounded-md text-violet-600 dark:text-violet-400 hover:bg-violet-500/10 transition-colors"
            data-testid="value-chart-expand-btn"
            title={isExpanded
              ? "Shrink the chart back to its normal size on the dashboard."
              : "Expand the chart to fill the screen — every marker and the hover hit-targets get bigger, making overlapping ▲/▼/● easier to pick out individually."}
            aria-label={isExpanded ? "Shrink chart" : "Expand chart"}
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Selector explainer — clarifies that the buttons only zoom
          the chart line, not the headline value. */}
      <p className="text-[10px] text-muted-foreground/70 italic mb-2" data-testid="value-chart-period-hint">
        {(() => {
          const effKey = data?.effective_period || period;
          const effLabel = PERIODS.find(p => p.key === effKey)?.label || (effKey || '').toUpperCase();
          const wasClamped = data?.effective_period && data?.requested_period && data.effective_period !== data.requested_period;
          if (wasClamped) {
            const reqLabel = PERIODS.find(p => p.key === data.requested_period)?.label || data.requested_period.toUpperCase();
            return `Your portfolio isn't ${reqLabel} old yet — showing the widest range it has data for (${effLabel}).`;
          }
          return `Showing the last ${effLabel}. Buttons zoom the chart line; headline value above stays lifetime.`;
        })()}
      </p>

      {/* Legend — every dot is clickable to toggle its layer.
          Counts surface "▲ N purchases / ▼ N sells / ● N dividends"
          at a glance. */}
      {data && chart && (
        <div className="flex flex-wrap items-center gap-2 mb-2 pb-2 border-b border-border/40" data-testid="chart-legend">
          <LegendDot keyName="value" color={lineColor} label="Value" />
          <LegendDot keyName="cost" color={costColor} label="Cost basis" />
          <LegendDot keyName="benchmark" color={benchColor} label={`${data?.benchmark_ticker || benchmarkTicker} benchmark`} />
          {data.purchases?.length > 0 && (
            <LegendDot keyName="purchases" color={purchaseColor} label="▲ Buys" count={data.purchases.length} />
          )}
          {data.sells?.length > 0 && (
            <LegendDot keyName="sells" color={sellColor} label="▼ Sells" count={data.sells.length} />
          )}
          {data.dividends?.length > 0 && (
            <LegendDot keyName="dividends" color={dividendColor} label="● Dividends" count={data.dividends.length} />
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : !chart ? (
        <div className="flex flex-col items-center justify-center h-48 text-muted-foreground text-xs gap-1 px-4 text-center">
          <span data-testid="chart-empty-message">No chart data for the last {PERIODS.find(p => p.key === period)?.label || (period || '').toUpperCase()}.</span>
          <span className="text-[10px] opacity-80">Try a wider period like 6m or 1y if your portfolio is brand-new.</span>
        </div>
      ) : (
        <div ref={containerRef} className="relative" onMouseMove={handleMouseMove} onMouseLeave={() => { setTooltip(null); setMarkerTooltip(null); }}>
          <svg viewBox={`0 0 ${chart.w} ${chart.h}`} className="w-full h-auto">
            {chart.yTicks.map((t, i) => (
              <g key={i}>
                <line x1={chart.pl} y1={t.y} x2={chart.pl + chart.cw} y2={t.y} className="stroke-border/20" strokeDasharray="3,3" />
                <text x={chart.pl - 5} y={t.y + 3} textAnchor="end" className="fill-muted-foreground" fontSize="12" fontFamily="monospace" fontWeight="600">{t.val}</text>
              </g>
            ))}
            {chart.xTicks.map((t, i) => {
              // Round 95bw v2 — 1M-only month tinting. The 3M/6M views
              // keep the muted-foreground color so a chart with three
              // months of data doesn't suddenly look like a rainbow.
              const colorPair = MONTH_COLORS[t.month] || ['#64748b', '#94a3b8'];
              const fill = t.colored ? (isDark ? colorPair[1] : colorPair[0]) : undefined;
              return (
                <text
                  key={i}
                  x={t.x}
                  y={chart.h - 5}
                  textAnchor="middle"
                  className={t.colored ? '' : 'fill-muted-foreground'}
                  fill={fill}
                  fontSize="12"
                  fontFamily="monospace"
                  fontWeight="600"
                >{t.label}</text>
              );
            })}
            {/* Cost basis (dashed slate) — drawn first so the value line
                renders on top. Hidden when toggled off. */}
            {layers.cost && chart.costD && (
              <path d={chart.costD} fill="none" stroke={costColor} strokeWidth="1.5" strokeDasharray="5,4" strokeLinejoin="round" data-testid="chart-cost-basis-line" />
            )}
            {/* SPY benchmark — solid violet. */}
            {layers.benchmark && chart.benchD && (
              <path d={chart.benchD} fill="none" stroke={benchColor} strokeWidth="1.5" strokeLinejoin="round" data-testid="chart-benchmark-line" />
            )}
            {/* Portfolio value — primary line + filled area. */}
            {layers.value && (
              <>
                <path d={chart.areaD} fill={areaColor} />
                <path d={chart.lineD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" />
              </>
            )}
            {/* Purchase markers (amber ▲) */}
            {layers.purchases && chart.purchaseMarkers.map((m, i) => {
              const triPath = `M ${m.x} ${m.y - 16} L ${m.x - 5} ${m.y - 6} L ${m.x + 5} ${m.y - 6} Z`;
              return (
                <g
                  key={`buy-${i}`}
                  className="cursor-pointer"
                  onMouseEnter={() => {
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setMarkerTooltip({
                      kind: 'purchase',
                      items: m.items,
                      screenX: (m.x / chart.w) * rect.width,
                      screenY: (m.y / chart.h) * rect.height,
                    });
                  }}
                  onMouseLeave={() => setMarkerTooltip(null)}
                  data-testid={`purchase-marker-${m.items[0]?.actual_date || i}`}
                >
                  <path d={triPath} fill={purchaseColor} stroke={isDark ? '#1f2937' : '#fff'} strokeWidth="1.2" />
                  <circle cx={m.x} cy={m.y - 10} r="10" fill="transparent" />
                </g>
              );
            })}
            {/* Sell markers (red ▼) */}
            {layers.sells && chart.sellMarkers.map((m, i) => {
              const triPath = `M ${m.x} ${m.y + 16} L ${m.x - 5} ${m.y + 6} L ${m.x + 5} ${m.y + 6} Z`;
              return (
                <g
                  key={`sell-${i}`}
                  className="cursor-pointer"
                  onMouseEnter={() => {
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setMarkerTooltip({
                      kind: 'sell',
                      items: m.items,
                      screenX: (m.x / chart.w) * rect.width,
                      screenY: (m.y / chart.h) * rect.height,
                    });
                  }}
                  onMouseLeave={() => setMarkerTooltip(null)}
                  data-testid={`sell-marker-${m.items[0]?.actual_date || i}`}
                >
                  <path d={triPath} fill={sellColor} stroke={isDark ? '#1f2937' : '#fff'} strokeWidth="1.2" />
                  <circle cx={m.x} cy={m.y + 10} r="10" fill="transparent" />
                </g>
              );
            })}
            {/* Dividend dots (sky blue ●) — pinned just under the line
                so they sit above the cost line but don't crowd ▲▼. */}
            {layers.dividends && chart.dividendDots.map((m, i) => (
              <g
                key={`div-${i}`}
                className="cursor-pointer"
                onMouseEnter={() => {
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  setMarkerTooltip({
                    kind: 'dividend',
                    items: m.items,
                    screenX: (m.x / chart.w) * rect.width,
                    screenY: (m.y / chart.h) * rect.height,
                  });
                }}
                onMouseLeave={() => setMarkerTooltip(null)}
                onClick={() => {
                  // Round P146·5 — tap-to-open persistent modal so
                  // phone users (no hover) can read the payout too.
                  setMarkerTooltip(null);
                  setDivModal({ kind: 'day', items: m.items });
                }}
                data-testid={`dividend-dot-${m.items[0]?.date || i}`}
              >
                <circle cx={m.x} cy={m.y + 4} r="3.5" fill={dividendColor} stroke={isDark ? '#1f2937' : '#fff'} strokeWidth="1" />
                <circle cx={m.x} cy={m.y + 4} r="9" fill="transparent" />
              </g>
            ))}
            {/* Round P146·5 — daily-payer monthly rollup ▲ (sky blue,
                below the line pointing up — distinct from amber buy ▲
                above the line and red sell ▼). One per ticker-month;
                click opens the persistent payouts modal. */}
            {layers.dividends && chart.dividendRollups.map((m, i) => {
              const triPath = `M ${m.x} ${m.y + 6} L ${m.x - 5.5} ${m.y + 17} L ${m.x + 5.5} ${m.y + 17} Z`;
              return (
                <g
                  key={`divroll-${i}`}
                  className="cursor-pointer"
                  onClick={() => setDivModal({ kind: 'month', ...m })}
                  data-testid={`dividend-rollup-${m.ticker}-${m.month}`}
                >
                  <title>{`${m.items.length} ${m.ticker} daily payouts in ${formatMonthLabel(m.month)} — click for details`}</title>
                  <path d={triPath} fill={dividendColor} stroke={isDark ? '#1f2937' : '#fff'} strokeWidth="1.2" />
                  <circle cx={m.x} cy={m.y + 12} r="18" fill="transparent" />
                </g>
              );
            })}
            {tooltip && (
              <>
                <line x1={tooltip.svgX} y1={chart.pt} x2={tooltip.svgX} y2={chart.pt + chart.ch} stroke={isDark ? '#555' : '#ccc'} strokeWidth="1" strokeDasharray="4,2" />
                <circle cx={tooltip.svgX} cy={tooltip.svgY} r="4" fill={lineColor} stroke={isDark ? '#222' : '#fff'} strokeWidth="1.5" />
              </>
            )}
          </svg>
          {/* Round 95bw v2 (Jun 2026) — 1M-only month color key. One
              pill per month present in the data, positioned at the
              midpoint of that month's data points so it lines up with
              the colored dates above it. Hidden on other periods. */}
          {chart.monthLegend && chart.monthLegend.length > 0 && (
            <div className="relative w-full mt-1 mb-1 select-none" style={{ height: '18px' }} data-testid="value-chart-month-key">
              {chart.monthLegend.map((m, i) => {
                const pair = MONTH_COLORS[m.month] || ['#64748b', '#94a3b8'];
                const color = isDark ? pair[1] : pair[0];
                return (
                  <span
                    key={i}
                    className="absolute -translate-x-1/2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{
                      left: `${(m.midX / chart.w) * 100}%`,
                      color,
                      backgroundColor: isDark ? `${color}1a` : `${color}14`,
                      border: `1px solid ${color}66`,
                    }}
                    title={`Dates in ${m.label} are colored ${color}.`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                    {m.label}
                  </span>
                );
              })}
            </div>
          )}
          {tooltip && (() => {
            // Flip the tooltip to the LEFT of the cursor on the right
            // half of the chart so it doesn't cover ▲ ▼ ● markers the
            // user is trying to hover. Width estimate (~180px) keeps
            // the flip-point sane regardless of which layers are on.
            const containerW = containerRef.current?.clientWidth || 400;
            const tooltipW = isExpanded ? 210 : 180;
            const onRightHalf = tooltip.screenX > containerW / 2;
            const left = onRightHalf
              ? Math.max(8, tooltip.screenX - tooltipW - 10)
              : Math.min(tooltip.screenX + 10, containerW - tooltipW - 8);
            return (
              <div
                className={`absolute z-10 pointer-events-none bg-card border rounded-md shadow-lg px-2.5 py-1.5 ${isExpanded ? 'text-[12px] min-w-[180px]' : 'text-[10px] min-w-[150px]'}`}
                style={{
                  left: `${left}px`,
                  top: `${Math.max(tooltip.screenY - 60, 0)}px`,
                }}
                data-testid="value-tooltip"
              >
                <p className="font-semibold mb-1">{tooltip.date}</p>
                <p className="font-mono flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: lineColor }} />
                  Value: <span className="font-bold ml-auto">{formatValue(tooltip.value)}</span>
                </p>
                {layers.cost && tooltip.costBasis !== null && (
                  <p className="font-mono text-muted-foreground flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: costColor }} />
                    Cost: <span className="ml-auto">{formatValue(tooltip.costBasis)}</span>
                  </p>
                )}
                {tooltip.gain !== null && (
                  <p className={`font-mono font-bold ${tooltip.gain >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {tooltip.gain >= 0 ? '+' : ''}{formatValue(Math.abs(tooltip.gain))}
                    {tooltip.gainPct !== null && ` (${tooltip.gainPct >= 0 ? '+' : ''}${tooltip.gainPct.toFixed(2)}%)`}
                  </p>
                )}
                {layers.benchmark && tooltip.benchmark !== null && (
                  <p className="font-mono text-muted-foreground flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: benchColor }} />
                    {data?.benchmark_ticker || benchmarkTicker}: <span className="ml-auto">{formatValue(tooltip.benchmark)}</span>
                  </p>
                )}
              </div>
            );
          })()}
          {markerTooltip && (() => {
            // Same flip-on-right-half rule as the value tooltip so the
            // marker card doesn't sit on top of other ▲ ▼ ● glyphs the
            // user might want to inspect next.
            const containerW = containerRef.current?.clientWidth || 400;
            const tooltipW = isExpanded ? 240 : 200;
            const onRightHalf = markerTooltip.screenX > containerW / 2;
            const left = onRightHalf
              ? Math.max(8, markerTooltip.screenX - tooltipW - 10)
              : Math.min(markerTooltip.screenX + 10, containerW - tooltipW - 8);
            return (
              <div
                className={`absolute z-20 pointer-events-none bg-card border-2 rounded-md shadow-lg px-2.5 py-1.5 ${isExpanded ? 'text-[12px] min-w-[210px]' : 'text-[10px] min-w-[180px]'}`}
                style={{
                  left: `${left}px`,
                  top: `${Math.max(markerTooltip.screenY - 50, 0)}px`,
                  borderColor: markerTooltip.kind === 'sell' ? sellColor + '99' : markerTooltip.kind === 'dividend' ? dividendColor + '99' : purchaseColor + '99',
                }}
                data-testid="marker-tooltip"
              >
                <p
                  className="font-bold uppercase tracking-wider text-[9px] mb-1"
                  style={{ color: markerTooltip.kind === 'sell' ? sellColor : markerTooltip.kind === 'dividend' ? dividendColor : purchaseColor }}
                >
                  {markerTooltip.kind === 'sell'
                    ? (markerTooltip.items.length === 1 ? 'Sale' : `${markerTooltip.items.length} Sales`)
                    : markerTooltip.kind === 'dividend'
                      ? (markerTooltip.items.length === 1 ? 'Dividend Paid' : `${markerTooltip.items.length} Dividends Paid`)
                      : (markerTooltip.items.length === 1 ? 'Purchase' : `${markerTooltip.items.length} Purchases`)
                  }
                </p>
                {markerTooltip.items.map((it, idx) => (
                  <div key={idx} className={idx > 0 ? 'mt-1.5 pt-1.5 border-t border-border/40' : ''}>
                    <p className="font-semibold">
                      {it.ticker} <span className="text-muted-foreground font-normal">— {it.actual_date || it.date}</span>
                    </p>
                    {markerTooltip.kind === 'purchase' && (
                      <>
                        <p className="font-mono text-muted-foreground">
                          {it.shares} sh × ${it.cost_per_share?.toFixed(2)} = <span className="text-foreground font-semibold">${it.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </p>
                        {it.cash_source && it.cash_source !== 'outside' && (
                          <p className="font-mono text-[9px] text-orange-600 dark:text-orange-400 mt-0.5">
                            {it.cash_source === 'inside' ? 'Paid from inside cash' : `Split: $${it.inside_amount?.toFixed(2)} inside / $${it.outside_amount?.toFixed(2)} outside`}
                          </p>
                        )}
                      </>
                    )}
                    {markerTooltip.kind === 'sell' && (
                      <p className="font-mono text-muted-foreground">
                        {it.shares} sh × ${it.sale_price?.toFixed(2)} = <span className="text-foreground font-semibold">${it.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </p>
                    )}
                    {markerTooltip.kind === 'dividend' && (
                      <p className="font-mono text-muted-foreground">
                        {it.shares} sh × ${it.per_share?.toFixed(4)} = <span className="text-foreground font-semibold">${it.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Round P146·5 — Persistent dividend payouts modal. Opened by
          clicking a monthly rollup ▲ (daily payers) or any ● dividend
          dot (phone-friendly). Stays open until the X, the backdrop,
          or Escape — never auto-dismisses on mouse-out. Scrollable so
          a full month of ~22 daily payouts fits on any screen. */}
      {divModal && (() => {
        const items = divModal.items || [];
        const total = divModal.kind === 'month'
          ? divModal.total
          : items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
        const title = divModal.kind === 'month'
          ? `${divModal.ticker} — ${formatMonthLabel(divModal.month)}`
          : `Dividends — ${formatDateLabel(items[0]?.date || '')}`;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
            onClick={() => setDivModal(null)}
            data-testid="dividend-payouts-modal"
          >
            <div
              className="bg-card border rounded-xl shadow-2xl w-full max-w-sm relative max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                <div>
                  <h3 className="text-sm font-bold flex items-center gap-2" data-testid="dividend-payouts-modal-title">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dividendColor }} />
                    {title}
                  </h3>
                  <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                    {items.length} {items.length === 1 ? 'payout' : 'payouts'} · total{' '}
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">
                      ${Number(total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => setDivModal(null)}
                  className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors shrink-0"
                  aria-label="Close"
                  data-testid="dividend-payouts-modal-close"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={3} />
                </button>
              </div>
              <div className="p-3 overflow-y-auto divide-y divide-border/40">
                {items.map((it, idx) => (
                  <div
                    key={idx}
                    className="py-1.5 first:pt-0 last:pb-0 flex items-center justify-between gap-2"
                    data-testid={`dividend-payout-row-${it.ticker}-${it.date}`}
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-semibold font-mono">
                        {it.ticker} <span className="text-muted-foreground font-normal">— {it.date}</span>
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground">
                        {it.shares} sh × ${it.per_share?.toFixed(4)}
                      </p>
                    </div>
                    <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 shrink-0 tabular-nums">
                      ${it.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Date-pair comparison — let the user pick two dates inside the
          visible chart period and get the exact $ delta + % gain
          between them. Day input auto-clamps to the days-in-month for
          the selected month, and out-of-range picks raise a toast
          pointing to the valid window. */}
      {data && chart && compareRange && (
        <div className="mt-3 pt-3 border-t border-border/40" data-testid="chart-compare-form">
          <div className="flex items-baseline justify-between mb-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Compare two dates
            </p>
            <p className="text-[9px] font-mono text-muted-foreground/70">
              valid: {fmtIso(compareRange.earliest)} → {fmtIso(compareRange.latest)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { idx: 1, pick: d1, color: '#0ea5e9' },
              { idx: 2, pick: d2, color: '#22c55e' },
            ].map(({ idx, pick, color }, i) => (
              <div key={idx} className="flex items-center gap-1.5">
                {i === 1 && (
                  <span className="text-[10px] font-bold text-muted-foreground px-1">vs</span>
                )}
                <select
                  value={pick.month}
                  onChange={(e) => updatePick(idx, { month: e.target.value })}
                  className="h-7 rounded border border-border bg-background px-1.5 text-[11px] font-mono"
                  data-testid={`compare-month-${idx}`}
                >
                  <option value="">Month</option>
                  {MONTHS.map((name, mIdx) => (
                    <option key={mIdx + 1} value={String(mIdx + 1)}>{name}</option>
                  ))}
                </select>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max={daysInMonth(pick.year, pick.month)}
                    placeholder="Day"
                    value={pick.day}
                    onChange={(e) => handleDayChange(idx, e.target.value)}
                    className="h-7 w-14 rounded-l border border-r-0 border-border bg-background px-1.5 text-[11px] font-mono text-center"
                    data-testid={`compare-day-${idx}`}
                  />
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => bumpDay(idx, +1)}
                      className="h-3.5 w-5 border border-border bg-muted/30 hover:bg-muted/60 rounded-tr flex items-center justify-center"
                      data-testid={`compare-day-${idx}-up`}
                      tabIndex={-1}
                      aria-label="Day up"
                    >
                      <ChevronUp className="w-2.5 h-2.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => bumpDay(idx, -1)}
                      className="h-3.5 w-5 border border-t-0 border-border bg-muted/30 hover:bg-muted/60 rounded-br flex items-center justify-center"
                      data-testid={`compare-day-${idx}-down`}
                      tabIndex={-1}
                      aria-label="Day down"
                    >
                      <ChevronDown className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </div>
                <select
                  value={pick.year}
                  onChange={(e) => updatePick(idx, { year: e.target.value })}
                  className="h-7 rounded border border-border bg-background px-1.5 text-[11px] font-mono"
                  data-testid={`compare-year-${idx}`}
                >
                  <option value="">Year</option>
                  {yearList.map(y => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
                <span
                  className="text-[10px] font-mono"
                  style={{ color }}
                  data-testid={`compare-resolved-${idx}`}
                >
                  {(() => {
                    const r = resolvePick(pick);
                    if (!r) return '—';
                    if (r.outOfRange) return 'out of range';
                    return `${fmtIso(r.point.date)} · ${formatValue(r.point.value)}`;
                  })()}
                </span>
              </div>
            ))}
            {compareResult && (
              <div
                className={`ml-auto inline-flex items-center gap-2 text-[11px] font-mono px-2 py-1 rounded-md font-bold ${
                  compareResult.delta >= 0
                    ? 'bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-400'
                    : 'bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400'
                }`}
                data-testid="compare-result"
              >
                {compareResult.delta >= 0 ? '+' : '−'}{formatValue(Math.abs(compareResult.delta))}
                {compareResult.pct !== null && (
                  <span className="opacity-80">
                    ({compareResult.pct >= 0 ? '+' : ''}{compareResult.pct.toFixed(2)}%)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}

// ─── Sector Allocation Doughnut ───
function SectorDoughnut({ isDark, refreshKey, onStockClick, onRebalancerClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bump, setBump] = useState(0);
  const [hovered, setHovered] = useState(null); // sector name or null
  // Locked sector — set on click (arc or legend row). Persists until
  // another sector is clicked. Hover still wins for transient preview,
  // but releasing the hover snaps back to the locked sector instead of
  // the default. ESC clears the lock.
  const [locked, setLocked] = useState(null);
  const toggleLock = useCallback((sector) => {
    setLocked((cur) => (cur === sector ? null : sector));
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLocked(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // Round 95bx (Jun 2026) — Desktop-only "open full-size" affordance.
  // Mirrors the Portfolio Value expand toggle. Two intentional
  // differences from that one:
  //   1. NO backdrop-click-to-close. The user scrolls inside this
  //      modal to find their sectors' tickers; releasing the mouse
  //      off the scrollbar would slam the modal shut mid-scroll.
  //      ESC + the red top-right X are the only close paths.
  //   2. Uses ModalCloseButton (the app's "special X closer" — blue
  //      outline → red on hover, hold 1.5s to force-remove from DOM).
  //      Identical to the StockModal close affordance.
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isExpanded]);
  // Round 92o++++++++N++++++++++ (Jun 2026) — Plus multi-portfolio.
  // Scope params so the sector doughnut reflects ONLY the active
  // portfolio's lots. Previously the doughnut kept the All view's
  // $20K when the user switched to a single portfolio — the user
  // saw "Sector Allocation TOTAL $20.0K" lingering on a $4K
  // portfolio view.
  const scopeParams = usePortfolioScopeParams();
  const scopeKey = scopeParams.portfolio_id || 'all';

  // Round 87 (Feb 2026) — Silent background refresh. Only flip the
  // `loading` flag on the *initial* fetch (when we have no data yet).
  // Subsequent refetches (cash-changed, portfolio-changed, live-prices-
  // tick) run in the background; we keep the existing chart on-screen
  // and let React diff the new arcs in when `setData` resolves. This
  // prevents the whole card from unmounting → spinner → remount, which
  // was causing a page-wide layout jump on mobile every 5 seconds.
  // Round P124 (Feb 2026) — Gate the Sector Allocation on stage 3
  // (after summary + capital tracker). Advances to stage 4 when
  // done so the middle-of-page fetches (positions, dividend-
  // calendar, transactions, risk-score, heatmap, etc.) can fire.
  const sectorLoadStage = usePortfolioLoadStage();
  const sectorReqSeq = useRef(0);
  const sectorPrevScope = useRef(scopeKey);
  useEffect(() => {
    if (sectorPrevScope.current === scopeKey) return;
    sectorPrevScope.current = scopeKey;
    setData(null);
  }, [scopeKey]);

  useEffect(() => {
    if (sectorLoadStage < 3) return undefined;
    const seq = ++sectorReqSeq.current;
    const isInitial = data === null;
    if (isInitial) setLoading(true);
    axios.get(`${API}/portfolio/sector-allocation`, { params: scopeParams, withCredentials: true })
      .then(res => { if (seq === sectorReqSeq.current) setData(res.data); })
      .catch(() => { if (seq === sectorReqSeq.current && isInitial) setData(null); })
      .finally(() => { if (seq === sectorReqSeq.current && isInitial) setLoading(false); })
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, bump, scopeKey, sectorLoadStage]);

  // Refresh when cash changes
  useEffect(() => {
    const h = () => setBump((b) => b + 1);
    window.addEventListener('cash-changed', h);
    window.addEventListener('portfolio-changed', h);
    // Round 86c (May 2026) — Live price ticks. PortfolioSummary
    // debounces them and rebroadcasts as `live-prices-tick` so all
    // price-sensitive cards (charts, sector allocation, Net Worth
    // sparkline) refresh together instead of going stale until the
    // user navigates away and back.
    window.addEventListener('live-prices-tick', h);
    return () => {
      window.removeEventListener('cash-changed', h);
      window.removeEventListener('portfolio-changed', h);
      window.removeEventListener('live-prices-tick', h);
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 flex-1 min-w-[280px] flex items-center justify-center h-[280px]">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.sectors || data.sectors.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-4 flex-1 min-w-[280px] flex items-center justify-center h-[280px] text-muted-foreground text-xs">
        No sector data
      </div>
    );
  }

  const colorIdx = isDark ? 1 : 0;
  const size = 160;
  const cx = size / 2, cy = size / 2;
  const outerR = 70, innerR = 42;

  // Build doughnut arcs
  // Cash slice always gets a distinct neutral green-gray color (theme-aware).
  const CASH_COLOR = isDark ? '#94a3b8' : '#64748b'; // slate
  let startAngle = -90;
  const arcs = data.sectors.map((s, i) => {
    const color = s.sector === 'Cash'
      ? CASH_COLOR
      : SECTOR_COLORS[i % SECTOR_COLORS.length][colorIdx];
    const angle = (s.pct / 100) * 360;

    // Full circle can't be drawn as a single arc — use two circles
    if (angle >= 359.9) {
      startAngle += angle;
      return { type: 'circle', color, ...s };
    }

    const endAngle = startAngle + angle;
    const largeArc = angle > 180 ? 1 : 0;

    const toRad = (a) => (a * Math.PI) / 180;
    const x1o = cx + outerR * Math.cos(toRad(startAngle));
    const y1o = cy + outerR * Math.sin(toRad(startAngle));
    const x2o = cx + outerR * Math.cos(toRad(endAngle));
    const y2o = cy + outerR * Math.sin(toRad(endAngle));
    const x1i = cx + innerR * Math.cos(toRad(endAngle));
    const y1i = cy + innerR * Math.sin(toRad(endAngle));
    const x2i = cx + innerR * Math.cos(toRad(startAngle));
    const y2i = cy + innerR * Math.sin(toRad(startAngle));

    const d = `M ${x1o} ${y1o} A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2o} ${y2o} L ${x1i} ${y1i} A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2i} ${y2i} Z`;

    startAngle = endAngle;
    return { type: 'arc', d, color, ...s };
  });

  return (
    <>
      {/* Round 95bx (Jun 2026) — Translucent backdrop when expanded.
          UNLIKE the Portfolio Value chart, clicking the backdrop does
          NOT close this modal. The user scrolls inside it to inspect
          per-sector ticker tables; releasing the drag off the scroll
          track would slam the modal shut mid-scroll. ESC + the red X
          are the only close paths. */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm hidden md:block"
          data-testid="sector-allocation-expand-backdrop"
          aria-hidden="true"
        />
      )}
      <div
        className={isExpanded
          ? "sector-allocation-expanded-text fixed left-[2.5vw] right-[2.5vw] top-[3vh] bottom-[3vh] z-50 rounded-lg border bg-card p-6 overflow-y-auto shadow-2xl flex flex-col"
          : "rounded-lg border bg-card p-4 flex-1 min-w-[280px] flex flex-col"
        }
        data-testid="sector-allocation-chart"
        role={isExpanded ? "dialog" : undefined}
        aria-modal={isExpanded ? "true" : undefined}
        aria-label={isExpanded ? "Sector Allocation — expanded" : undefined}
      >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-bold flex items-center gap-1.5">
          <PieChart className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400 shrink-0" />
          <span>Sector Allocation</span>
        </h3>
        <div className="flex items-center gap-2 ml-auto">
          {/* Rebalancer — May 2026: relocated from the page-wide action
              bar into the right side of this card's title row, per user
              request. */}
          {onRebalancerClick && (
            <PortfolioActionLink
              icon={Scale}
              label="Rebalancer"
              onClick={onRebalancerClick}
              tone="text-sky-500 dark:text-sky-400"
              testId="portfolio-rebalancer-btn"
              title="Open the Rebalancer to see target weights and recommended buys/sells"
              sizeBoost={1}
              bordered
            />
          )}
          {/* Round 95bx (Jun 2026) — Expand/Shrink toggle. Desktop-only
              via `hidden md:inline-flex`. When expanded swaps to the
              ModalCloseButton (red X, hold-to-force-close) which sits
              in the same spot. */}
          {!isExpanded && (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              className="hidden md:inline-flex items-center justify-center p-1 rounded-md text-violet-600 dark:text-violet-400 hover:bg-violet-500/10 transition-colors"
              data-testid="sector-allocation-expand-btn"
              title="Expand the Sector Allocation to fill the screen — gives long ticker tables room to breathe and adds an internal scroll."
              aria-label="Expand sector allocation"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
          {isExpanded && (
            <ModalCloseButton
              onClick={() => setIsExpanded(false)}
              testId="sector-allocation-close-btn"
              label="Close expanded Sector Allocation"
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="relative shrink-0">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {arcs.map((arc, i) => {
              const isActive = hovered === arc.sector;
              const isLocked = locked === arc.sector;
              const commonProps = {
                onMouseEnter: () => setHovered(arc.sector),
                onMouseLeave: () => setHovered((h) => h === arc.sector ? null : h),
                onClick: () => toggleLock(arc.sector),
                style: {
                  cursor: 'pointer',
                  // Dim the non-focused slices when ANY slice is in
                  // focus (hover or lock), and keep the locked slice at
                  // full opacity even after the mouse leaves.
                  opacity: (hovered || locked) && !isActive && !isLocked ? 0.4 : 1,
                  transition: 'opacity 150ms',
                },
              };
              return arc.type === 'circle' ? (
                <g key={i} {...commonProps}>
                  <circle cx={cx} cy={cy} r={outerR} fill={arc.color} />
                  <circle cx={cx} cy={cy} r={innerR} className="fill-card" style={{ pointerEvents: 'none' }} />
                </g>
              ) : (
                <path key={i} d={arc.d} fill={arc.color} {...commonProps} />
              );
            })}
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total</span>
            <span className="text-sm font-bold font-mono">{formatValue(data.total_value)}</span>
          </div>
        </div>
        {/* Legend */}
        <div className="space-y-2.5 flex-1">
          {arcs.map((s, i) => {
            const isLocked = locked === s.sector;
            const isHovered = hovered === s.sector;
            return (
              <button
                key={i}
                type="button"
                className={`w-full flex items-center justify-between gap-2 rounded px-1 py-0.5 transition-colors cursor-pointer text-left ${
                  isLocked
                    ? 'bg-primary/10 ring-1 ring-primary/40 dark:bg-primary/20'
                    : isHovered
                      ? 'bg-muted'
                      : ''
                }`}
                onMouseEnter={() => setHovered(s.sector)}
                onMouseLeave={() => setHovered((h) => h === s.sector ? null : h)}
                onClick={() => toggleLock(s.sector)}
                title={isLocked ? `Click again to unlock ${s.sector}` : `Click to lock ${s.sector}`}
                data-testid={`sector-${s.sector}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-xs font-semibold text-foreground">{s.sector}</span>
                  {isLocked && (
                    <span className="text-[8px] font-bold uppercase tracking-wider text-primary px-1 rounded bg-primary/20" data-testid={`sector-locked-badge-${s.sector}`}>
                      locked
                    </span>
                  )}
                </div>
                <span className="text-xs font-mono font-medium text-muted-foreground">{s.pct}%</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Inline sector detail panel — replaces the old pop-up overlay.
          Renders directly under the doughnut + legend so the breakdown
          stays visible without covering anything. Uses theme-aware
          tokens so light + dark themes both stay legible. */}
      {(() => {
        // Display priority: hover (transient preview) > locked (sticky
        // selection set by click) > first sector (default). Releasing
        // the hover snaps back to the locked sector instead of the
        // default — that's the click-to-lock behaviour.
        const focusKey = hovered || locked || arcs[0]?.sector;
        const active = arcs.find((a) => a.sector === focusKey) || arcs[0];
        if (!active) return null;
        const tickerRows = active.tickers || [];
        return (
          <div
            className="mt-3 pt-3 border-t border-border/40"
            data-testid="sector-detail-panel"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="w-3 h-3 rounded-full shrink-0 ring-2 ring-offset-1 ring-offset-card" style={{ backgroundColor: active.color, boxShadow: `0 0 0 1px ${active.color}55` }} />
              <span className="text-sm font-bold truncate text-foreground">{active.sector}</span>
              <span className="ml-auto inline-flex items-baseline gap-1.5">
                <span className="text-xs font-mono font-bold text-foreground">{formatValue(active.value)}</span>
                <span className="text-[11px] font-mono font-bold text-foreground" data-testid="sector-detail-pct">({active.pct}%</span>
                <span className="text-[10px] font-mono text-muted-foreground">of portfolio)</span>
              </span>
            </div>
            {active.sector === 'Cash' ? (
              <p className="text-xs font-mono text-emerald-700 dark:text-emerald-400 font-bold" data-testid="sector-detail-cash">
                {formatValue(active.value)} sitting in your portfolio cash bucket.
              </p>
            ) : tickerRows.length === 0 ? (
              <p className="text-xs text-muted-foreground">No holdings in this sector.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="sector-detail-rows">
                {/* Round 95bx v4 (Jun 2026) — Real <table> instead of
                    per-row CSS grids. With separate grids each row
                    sized its columns independently, so a 4-decimal
                    share count would push the Avg Cost cell right
                    into Value. Tables natively share column widths
                    across <thead> + <tbody>, so each column ends up
                    exactly as wide as its widest cell — fixes both
                    the compact card and the expanded modal in one
                    shot. The Ticker column gets `width: 100%` and
                    every other column is `white-space: nowrap` so
                    leftover space falls to Ticker, never the
                    numeric columns. */}
                <table className="sector-ticker-table w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                      <th className="text-left pr-1.5 py-0.5 font-bold" style={{ width: '100%' }}>Ticker</th>
                      <th className="text-right px-1.5 py-0.5 font-bold whitespace-nowrap border-l border-border/30">Shares</th>
                      <th className="text-right px-1.5 py-0.5 font-bold whitespace-nowrap border-l border-border/30">Avg Cost</th>
                      <th className="text-right px-1.5 py-0.5 font-bold whitespace-nowrap border-l border-border/30">Value</th>
                      <th className="text-right pl-1.5 py-0.5 font-bold whitespace-nowrap border-l border-border/30">% of {active.sector === 'Other' ? 'sector' : active.sector.split(' ')[0]}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickerRows.map((t) => {
                      const sectorPct = active.value > 0 ? (t.value / active.value) * 100 : 0;
                      return (
                        <tr
                          key={t.ticker}
                          onClick={() => onStockClick?.(t.ticker)}
                          className="hover:bg-muted/40 transition-colors cursor-pointer"
                          data-testid={`sector-row-${t.ticker}`}
                          title={`Open ${t.ticker} stock modal`}
                        >
                          <td className="min-w-0 pr-1.5 py-1 align-middle">
                            {/* `flex-wrap` so the wide P/L chip drops to a
                                new line on phone widths instead of squeezing
                                the truncate'd ticker `<p>` down to 0px. On
                                desktop the chip still sits inline with the
                                ticker (plenty of column room) — only mobile
                                wraps. */}
                            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                              <span className="font-bold text-foreground truncate hover:text-primary hover:underline transition-colors">{t.ticker}</span>
                              {/* Gain/Loss chip next to the ticker — pure
                                  colour cue so you can scan winners/losers
                                  at a glance. Hidden when there's no real
                                  cost basis (avg_cost = 0 ⇒ pre-feature
                                  legacy data, can't compute honest delta). */}
                              {t.cost_basis > 0 && (() => {
                                const gain = t.value - t.cost_basis;
                                const pct = t.cost_basis > 0 ? (gain / t.cost_basis) * 100 : 0;
                                const positive = gain >= 0;
                                return (
                                  <span
                                    className={`shrink-0 text-[9px] font-mono font-bold px-1 py-px rounded leading-none ${
                                      positive
                                        ? 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-400'
                                        : 'bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400'
                                    }`}
                                    title={`P/L vs cost basis ${formatValue(t.cost_basis)}`}
                                    data-testid={`sector-row-${t.ticker}-gain`}
                                  >
                                    P/L {positive ? '+' : '−'}{formatValue(Math.abs(gain))} ({positive ? '+' : ''}{pct.toFixed(1)}%)
                                  </span>
                                );
                              })()}
                            </div>
                            {t.name && t.name !== t.ticker && (
                              <p className="text-[10px] text-muted-foreground truncate" title={t.name}>{t.name}</p>
                            )}
                          </td>
                          <td className="text-right font-mono text-[11px] text-muted-foreground px-1.5 py-1 whitespace-nowrap align-middle border-l border-border/30">
                            {t.shares?.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                          </td>
                          <td className="text-right font-mono text-[11px] text-muted-foreground px-1.5 py-1 whitespace-nowrap align-middle border-l border-border/30">
                            {t.avg_cost > 0 ? `$${t.avg_cost.toFixed(2)}` : '—'}
                          </td>
                          <td className="text-right font-mono text-[11px] font-semibold text-foreground px-1.5 py-1 whitespace-nowrap align-middle border-l border-border/30">
                            {formatValue(t.value)}
                          </td>
                          <td
                            className="text-right font-mono text-[11px] font-bold tabular-nums pl-1.5 py-1 whitespace-nowrap align-middle border-l border-border/30"
                            style={{ color: active.color }}
                          >
                            {sectorPct.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </div>
    </>
  );
}

export default function PortfolioCharts({ isDark, refreshKey, onStockClick, onRebalancerClick }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="portfolio-charts">
      <ValueChart isDark={isDark} refreshKey={refreshKey} />
      <SectorDoughnut isDark={isDark} refreshKey={refreshKey} onStockClick={onStockClick} onRebalancerClick={onRebalancerClick} />
    </div>
  );
}
